import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiService } from '../services/apiService';

const DisplayContext = createContext();

export const useDisplay = () => {
  const context = useContext(DisplayContext);
  if (!context) {
    throw new Error('useDisplay must be used within a DisplayProvider');
  }
  return context;
};

export const DisplayProvider = ({ children }) => {
  // Global user preferences (synced to server)
  const [globalShowCategories, setGlobalShowCategories] = useState(true);
  const [globalShowQuickAddButtons, setGlobalShowQuickAddButtons] = useState(true);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Per-list settings cache (fetched from server per list)
  const [listSettingsCache, setListSettingsCache] = useState({});

  // Load user preferences from server on mount
  useEffect(() => {
    loadUserPreferences();
  }, []);

  const loadUserPreferences = async () => {
    try {
      const response = await apiService.getDisplayPreferences();
      const { preferences } = response;
      
      setGlobalShowCategories(preferences.globalShowCategories ?? true);
      setGlobalShowQuickAddButtons(preferences.globalShowQuickAddButtons ?? true);
      setPreferencesLoaded(true);
    } catch (error) {
      console.error('Failed to load display preferences:', error);
      // Fall back to defaults
      setGlobalShowCategories(true);
      setGlobalShowQuickAddButtons(true);
      setPreferencesLoaded(true);
    }
  };

  // Update global setting
  const updateGlobalShowCategories = async (show) => {
    try {
      setGlobalShowCategories(show);
      await apiService.updateDisplayPreferences({ globalShowCategories: show });
    } catch (error) {
      console.error('Failed to update globalShowCategories:', error);
      // Revert on error
      setGlobalShowCategories(!show);
    }
  };

  // Update global quick add buttons setting
  const updateGlobalShowQuickAddButtons = async (show) => {
    try {
      setGlobalShowQuickAddButtons(show);
      await apiService.updateDisplayPreferences({ globalShowQuickAddButtons: show });
    } catch (error) {
      console.error('Failed to update globalShowQuickAddButtons:', error);
      // Revert on error
      setGlobalShowQuickAddButtons(!show);
    }
  };

  // Load list settings from server
  const loadListSettings = async (listId) => {
    try {
      const response = await apiService.getListDisplaySettings(listId);
      const { settings } = response;
      
      setListSettingsCache(prev => ({
        ...prev,
        [listId]: settings
      }));
      
      return settings;
    } catch (error) {
      console.error('Failed to load list display settings:', error);
      return { showCategories: 'global', showQuickAddButtons: 'global' };
    }
  };

  // Update list-specific setting
  const updateListCategorySetting = async (listId, setting) => {
    console.log('🔧 DisplayContext: updateListCategorySetting called with:', { listId, setting });
    
    try {
      // Update server with new personal settings approach
      await apiService.updateListDisplaySettings(listId, { 
        setting: 'showCategories', 
        value: setting 
      });
      console.log('🔧 DisplayContext: Personal settings API call successful');
      
      setListSettingsCache(prev => ({
        ...prev,
        [listId]: {
          ...prev[listId],
          showCategories: setting
        }
      }));
      console.log('🔧 DisplayContext: Cache updated');
    } catch (error) {
      console.error('Failed to update personal category setting:', error);
    }
  };

  // Update list-specific quick add buttons setting
  const updateListQuickAddSetting = async (listId, setting) => {
    console.log('🔧 DisplayContext: updateListQuickAddSetting called with:', { listId, setting });
    
    try {
      // Update server with new personal settings approach
      await apiService.updateListDisplaySettings(listId, { 
        setting: 'showQuickAddButtons', 
        value: setting 
      });
      console.log('🔧 DisplayContext: Personal settings API call successful');
      
      setListSettingsCache(prev => ({
        ...prev,
        [listId]: {
          ...prev[listId],
          showQuickAddButtons: setting
        }
      }));
      console.log('🔧 DisplayContext: Cache updated');
    } catch (error) {
      console.error('Failed to update personal quick add setting:', error);
    }
  };

  // Get effective setting for a specific list
  const getListCategorySetting = (listId) => {
    if (!preferencesLoaded) return true; // Default while loading
    
    const listSettings = listSettingsCache[listId];
    if (!listSettings) {
      // Load settings if not cached
      loadListSettings(listId);
      return globalShowCategories; // Use global as fallback
    }
    
    const setting = listSettings.showCategories;
    if (setting === undefined || setting === 'global') {
      return globalShowCategories;
    }
    return setting === 'show';
  };

  // Get effective quick add buttons setting for a specific list
  const getListQuickAddSetting = (listId) => {
    if (!preferencesLoaded) return true; // Default while loading
    
    const listSettings = listSettingsCache[listId];
    if (!listSettings) {
      // Load settings if not cached
      loadListSettings(listId);
      return globalShowQuickAddButtons; // Use global as fallback
    }
    
    const setting = listSettings.showQuickAddButtons;
    if (setting === undefined || setting === 'global') {
      return globalShowQuickAddButtons;
    }
    return setting === 'show';
  };

  // Get the current setting option for a list
  const getListCategoryOption = (listId) => {
    const listSettings = listSettingsCache[listId];
    return listSettings?.showCategories || 'global';
  };

  // Get the current quick add setting option for a list
  const getListQuickAddOption = (listId) => {
    const listSettings = listSettingsCache[listId];
    return listSettings?.showQuickAddButtons || 'global';
  };

  const value = {
    globalShowCategories,
    updateGlobalShowCategories,
    updateListCategorySetting,
    getListCategorySetting,
    getListCategoryOption,
    globalShowQuickAddButtons,
    updateGlobalShowQuickAddButtons,
    updateListQuickAddSetting,
    getListQuickAddSetting,
    getListQuickAddOption
  };

  return (
    <DisplayContext.Provider value={value}>
      {children}
    </DisplayContext.Provider>
  );
};
